import * as React from 'react';
import { withRouter, matchPath } from 'react-router';

import classNames from 'classnames';
import { inject, observer } from 'mobx-react';

import { getConfig, ROUTES } from '@constants';

import { ECommonCounters } from '@enums';

import { AppServiceWorker, counters, EBrowser, getUserAgentInfo, isIOS, Log } from '@utils';

import { ICurrentUser, UiStore } from '@stores';

import { Api, PWA } from '@api';

import { App as AppBase, IAppBaseProps } from '@base/App';
import { ScrollToTop } from '@base/components';
import { Error } from '@base/components/Error';
import { PageTitle } from '@base/components/PageTitle';
import { EStore } from '@base/enums';

import { InstallPwaBubble, MainMenu } from '@components';
import { AppProviders } from '@components/AppProviders';
import { TopMenu } from '@components/TopMenu';

import { Router, RouterPopups, routes } from './Router';

import * as s from './App.scss';

interface IAppProps extends IAppBaseProps {
	currentUserStore?: ICurrentUser;
	uiStore?: UiStore;
}

@inject(
	EStore.CURRENT_USER,
	EStore.UI,
)
@observer
export class AppComponent extends React.PureComponent<IAppProps> {

	constructor(props: IAppProps) {
		super(props);

		if (!IS_SSR && !getConfig().isPwaEnabled && caches) {
			AppServiceWorker.unregister();

			void caches.keys().then((cacheNames) => {
				cacheNames.forEach((cacheName) => {
					void caches.delete(cacheName);
				});
			});
		}
	}

	componentDidMount() {
		if (getConfig().isPwaEnabled) {
			const {
				isTablet,
				isMobile,
				browser,
			} = getUserAgentInfo();
			const { uiStore } = this.props;
			const isPwaInstallable = getConfig().isShowInstallBtn && !isIOS() && (isTablet || isMobile) && (browser === EBrowser.CHROME || browser === EBrowser.EDGE);

			Api.pwa.init({
				onToggleOnlineStatus: (isOnline) => {
					uiStore.toggleIsOnline(isOnline);
				},
				isInstallable: isPwaInstallable,
				onInit: async () => {
					const pwaIsInstalled = await PWA.isInstalled();

					setTimeout(() => {
						if (!pwaIsInstalled && uiStore.isInstallButtonVisible !== isPwaInstallable) {
							uiStore.toggleInstallButton(isPwaInstallable);
						}
					}, 1000);
				},
				afterInstallCallback: () => {
					counters.sendRB(ECommonCounters.PWA_INSTALLED);
					uiStore.toggleInstallButton(false);
				},
				beforeInstallCallback: (deferredPrompt) => {
					Log.message('PWA beforeInstallCallback');
				},
			});
		}
	}

	render() {
		const { currentUserStore, location } = this.props;
		const isAppView = Boolean(currentUserStore.isUserAuth && matchPath(location.pathname, { path: ROUTES.APP }));

		return (
			<AppBase
				{...this.props}
				routes={routes}
				render={({ error }) => {
					return (
						<div className={classNames(
							s.app,
							{ 'twa': getConfig().isTwa },
							{ [s.appFixed]: this.props.uiStore.slidingMenu.isVisible },
						)}>
							<PageTitle/>
							<AppProviders>
								{error
									? <Error error={error}/>
									: <>
										{isAppView ? null : <TopMenu/>}
										<div className={s.innerWrapper}>
											<ScrollToTop/>
											{isAppView ? null : <MainMenu/>}
											<Router/>
											<RouterPopups/>
											{(getUserAgentInfo().isMobile || getUserAgentInfo().isTablet) &&
												<InstallPwaBubble/>
											}
										</div>
									</>
								}
							</AppProviders>
						</div>
					);
				}}
			/>
		);
	}

}

export const App = withRouter(AppComponent);
